// src/lib/appReady.ts — the boot loader (index.html #boot-loader) stays up until the first screen has its data.
//
// Anything still loading what the first screen shows takes a hold; when the last hold goes and React has painted, the
// loader fades out (200ms) and is removed. Four seconds after boot it goes regardless, so a slow call never traps it.
import { useEffect, useRef } from 'react';

let holds = 0;
let done = false;
let pending = false;

const loaderEl = () => (typeof document === 'undefined' ? null : document.getElementById('boot-loader'));

function fadeOut() {
  done = true;
  const el = loaderEl();
  if (!el) return;
  el.style.transition = 'opacity 200ms ease';
  el.style.opacity = '0';
  el.style.pointerEvents = 'none';
  window.setTimeout(() => el.remove(), 220);
}

/** Fades the boot loader out once nothing holds it and React has painted (two frames from now). `force` skips the count. */
export function releaseBootLoader(force = false) {
  if (done) return;
  if (!loaderEl()) { done = true; return; }
  if (force) { fadeOut(); return; }
  if (holds > 0 || pending) return;
  pending = true;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    pending = false;
    if (!done && holds === 0) fadeOut();
  }));
}

/** Keeps the boot loader up until the returned function is called (calling it twice is harmless). */
export function holdAppReady(): () => void {
  if (done) return () => {};
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds -= 1;
    releaseBootLoader();
  };
}

/** Holds the boot loader while `loading` is true on the first load only; later refetches never hold it. */
export function useHoldWhileLoading(loading: boolean | undefined) {
  const settled = useRef(false);
  useEffect(() => {
    if (settled.current) return;
    if (!loading) { settled.current = true; return; }
    const release = holdAppReady();
    return release;
  }, [loading]);
}

// Safety net: whatever happens, the app shows four seconds after boot.
if (typeof window !== 'undefined') window.setTimeout(() => releaseBootLoader(true), 4000);

/*
 * The one-off brand intro after signing in (AppLayout plays it). Set on a successful sign-in or sign-up, cleared once
 * it has played and on sign-out, so a plain reload never shows it.
 */
const INTRO_KEY = 'lotus.intro';
export function markIntroPending() { try { sessionStorage.setItem(INTRO_KEY, 'pending'); } catch { /* storage blocked: no intro */ } }
export function clearIntro() { try { sessionStorage.removeItem(INTRO_KEY); } catch { /* storage blocked */ } }
export function introPending(): boolean { try { return sessionStorage.getItem(INTRO_KEY) === 'pending'; } catch { return false; } }
